import { ChangeDetectorRef, Component, NgZone, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { fuseAnimations } from "@fuse/animations";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { NotificationChannelModel, NotificationChannelModelType } from "app/models/notificationChannel.model";
import { DateFormatValues, ProjectModel } from "app/models/project.model";
import { ProjectService } from "app/services/api/project.service";
import * as moment from "moment";
import "moment-timezone";
import { ToastrService } from "ngx-toastr";
import { timer } from "rxjs";
import { debounce } from "rxjs/operators";
import { locale as catalan } from "../../i18n/ca";
import { locale as english } from "../../i18n/en";
import { locale as spanish } from "../../i18n/es";
import { ProjectNotificationChannelAddEditComponent } from "../project-notification-channel-add-edit/project-notification-channel-add-edit.component";

@Component({
    selector: "app-project-admin",
    templateUrl: "./project-admin.component.html",
    styleUrls: ["./project-admin.component.scss"],
    animations: fuseAnimations,
    standalone: false,
})
export class ProjectAdminComponent implements OnInit {
    project: ProjectModel;
    public projectForm: UntypedFormGroup;
    pageType: string;
    savedChangesString: string;
    creationSuccessString: string;
    date: number;
    timezone: string;
    readonly notificationChannelDisplayedColumns = ["name", "type", "enabled", "buttons"];
    readonly testingNotificationChannelIds = new Set<string>();

    private syncingProjectForm = false;

    constructor(
        private translation: FuseTranslationLoaderService,
        private projectService: ProjectService,
        private route: ActivatedRoute,
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private toastr: ToastrService,
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef,
        private matDialog: MatDialog,
    ) {
        this.translation.loadTranslations(english, spanish, catalan);
        this.pageType = "new";

        translation.translateService.get("GLOBAL", { value: "SavedChanges" }).subscribe((res) => {
            this.savedChangesString = res.SavedChanges;
        });
        translation.translateService.get("GLOBAL", { value: "CreatedSuccess" }).subscribe((res) => {
            this.creationSuccessString = res.CreatedSuccess;
        });
    }

    ngOnInit() {
        this.project = this.normalizeProject(this.route.snapshot.data.data);
        if (this.project.id) {
            this.pageType = "edit";
        }

        const guessedTimezone = moment.tz.guess();
        this.projectForm = this.formBuilder.group({
            id: [this.project.id],
            name: [this.project.name, [Validators.required]],
            timezone: [this.pageType === "edit" ? this.project.timezone : guessedTimezone, [Validators.required]],
            dateformat: [this.pageType === "edit" ? this.project.dateformat : DateFormatValues[0], [Validators.required]],
            notificationChannels: this.buildNotificationChannelsFormArray(this.project.notificationChannels ?? []),
        });

        this.projectForm.valueChanges.pipe(debounce(() => timer(1000))).subscribe(() => {
            if (this.pageType !== "edit" || this.syncingProjectForm) {
                return;
            }
            this.save();
        });

        this.timezone = moment.tz(this.project.timezone).format("Z").replace(":", "");

        this.date = Date.now();
        this.ngZone.runOutsideAngular(() => {
            setInterval(() => {
                this.ngZone.run(() => {
                    this.date = Date.now();
                    this.cdr.detectChanges();
                });
            }, 1000);
        });
    }

    get timeZones() {
        return moment.tz.names();
    }

    get dateformats() {
        return DateFormatValues;
    }

    get notificationChannelsFormArray(): UntypedFormArray {
        return this.projectForm.get("notificationChannels") as UntypedFormArray;
    }

    get notificationChannelRows(): AbstractControl[] {
        return [...this.notificationChannelsFormArray.controls];
    }

    addNotificationChannel() {
        const dialogRef = this.openNotificationChannelPanel();
        dialogRef.afterClosed().subscribe((result) => {
            if (!result?.channel) {
                return;
            }

            if (this.pageType !== "edit" || !this.project?.id) {
                this.syncingProjectForm = true;
                this.notificationChannelsFormArray.push(this.buildNotificationChannelGroup(result.channel));
                this.notificationChannelsFormArray.markAsDirty();
                this.projectForm.markAsDirty();
                this.syncingProjectForm = false;
                return;
            }

            this.projectService.addNotificationChannel(this.project.id, result.channel).subscribe(
                (channel) => {
                    this.updateProjectNotificationChannels([
                        ...(this.project.notificationChannels ?? []),
                        channel,
                    ]);
                    this.toastr.success(this.savedChangesString);
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error");
                },
            );
        });
    }

    editNotificationChannel(index: number) {
        const channel = this.buildNotificationChannelModel(this.notificationChannelsFormArray.at(index)?.getRawValue());
        const dialogRef = this.openNotificationChannelPanel(channel);
        dialogRef.afterClosed().subscribe((result) => {
            if (!result?.channel) {
                return;
            }

            if (this.pageType !== "edit" || !this.project?.id || !channel?.id) {
                this.syncingProjectForm = true;
                this.notificationChannelsFormArray.setControl(index, this.buildNotificationChannelGroup(result.channel));
                this.notificationChannelsFormArray.markAsDirty();
                this.projectForm.markAsDirty();
                this.syncingProjectForm = false;
                return;
            }

            this.projectService
                .editNotificationChannel(this.project.id, channel.id, {
                    ...result.channel,
                    id: channel.id,
                })
                .subscribe(
                    (updatedChannel) => {
                        const nextChannels = [...(this.project.notificationChannels ?? [])];
                        const projectChannelIndex = nextChannels.findIndex((candidate) => candidate.id === updatedChannel.id);
                        if (projectChannelIndex >= 0) {
                            nextChannels[projectChannelIndex] = updatedChannel;
                        }
                        this.updateProjectNotificationChannels(nextChannels);
                        this.toastr.success(this.savedChangesString);
                    },
                    (error) => {
                        console.log(error);
                        this.toastr.error("Error");
                    },
                );
        });
    }

    removeNotificationChannel(index: number, event?: Event) {
        event?.stopPropagation();
        const channel = this.buildNotificationChannelModel(this.notificationChannelsFormArray.at(index)?.getRawValue());

        if (this.pageType !== "edit" || !this.project?.id || !channel?.id) {
            this.syncingProjectForm = true;
            this.notificationChannelsFormArray.removeAt(index);
            this.notificationChannelsFormArray.markAsDirty();
            this.projectForm.markAsDirty();
            this.syncingProjectForm = false;
            return;
        }

        this.projectService.deleteNotificationChannel(this.project.id, channel.id).subscribe(
            () => {
                this.updateProjectNotificationChannels(
                    (this.project.notificationChannels ?? []).filter((candidate) => candidate.id !== channel.id),
                );
                this.toastr.success(this.savedChangesString);
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error");
            },
        );
    }

    setNotificationChannelEnabled(channelControl: AbstractControl, enabled: boolean) {
        const channel = this.buildNotificationChannelModel(channelControl.getRawValue());

        if (this.pageType !== "edit" || !this.project?.id || !channel?.id) {
            channelControl.get("enabled")?.setValue(enabled, { emitEvent: false });
            channelControl.markAsDirty();
            this.projectForm.markAsDirty();
            return;
        }

        this.projectService.editNotificationChannel(this.project.id, channel.id, { ...channel, enabled }).subscribe(
            (updatedChannel) => {
                const nextChannels = [...(this.project.notificationChannels ?? [])];
                const projectChannelIndex = nextChannels.findIndex((candidate) => candidate.id === updatedChannel.id);
                if (projectChannelIndex >= 0) {
                    nextChannels[projectChannelIndex] = updatedChannel;
                }
                this.updateProjectNotificationChannels(nextChannels);
                this.toastr.success(this.savedChangesString);
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error");
            },
        );
    }

    sendNotificationChannelTest(channelControl: AbstractControl, event?: Event) {
        event?.stopPropagation();

        const channel = this.buildNotificationChannelModel(channelControl.getRawValue());
        if (this.pageType !== "edit" || !this.project?.id || !channel?.id) {
            return;
        }
        this.executeNotificationChannelTest(channel);
    }

    getNotificationChannelIndex(channelControl: AbstractControl): number {
        return this.notificationChannelsFormArray.controls.indexOf(channelControl);
    }

    getChannelTypeLabel(type: NotificationChannelModelType | string): string {
        return this.translation.translateService.instant(`PROJECTS.NotificationChannelType.${type}`);
    }

    isTestingNotificationChannel(channelControl: AbstractControl): boolean {
        const channelId = channelControl.get("id")?.value;
        return !!channelId && this.testingNotificationChannelIds.has(channelId);
    }

    add() {
        if (this.projectForm.invalid) {
            return;
        }
        const projectPayload = this.buildProjectPayload();
        this.projectService.create(projectPayload).subscribe(
            (data) => {
                this.toastr.success(this.creationSuccessString);
                this.project = this.normalizeProject(data);
                this.syncProjectForm(this.project);
                this.pageType = "edit";
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error");
            },
        );
    }

    save() {
        if (!this.project?.id || this.projectForm.invalid) {
            return;
        }
        const projectPayload = this.buildProjectPayload();
        this.projectService.edit(projectPayload).subscribe(
            (data) => {
                this.project = this.normalizeProject(data);
                this.syncProjectForm(this.project);
                this.timezone = moment.tz(this.project.timezone).format("Z").replace(":", "");
                this.toastr.success(this.savedChangesString);
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error");
                if (error.error.statusCode === 404) {
                    this.router.navigate(["/admin/projects"]);
                }
            },
        );
    }

    cancel() {
        this.router.navigate(["/admin/projects"]);
    }

    private normalizeProject(project?: ProjectModel): ProjectModel {
        const normalizedProject = project?.id ? project : new ProjectModel();
        normalizedProject.notificationChannels = normalizedProject.notificationChannels ?? [];
        normalizedProject.periods = normalizedProject.periods ?? [];
        return normalizedProject;
    }

    private buildNotificationChannelGroup(channel?: NotificationChannelModel): UntypedFormGroup {
        const normalizedChannel = channel ?? new NotificationChannelModel();
        const config = normalizedChannel.config ?? {};

        return this.formBuilder.group({
            id: [normalizedChannel.id],
            name: [normalizedChannel.name, [Validators.required]],
            type: [normalizedChannel.type ?? NotificationChannelModelType.Email, [Validators.required]],
            enabled: [normalizedChannel.enabled !== false],
            emailsInput: [(config.emails ?? []).join(", ")],
            phoneNumbersInput: [(config.phoneNumbers ?? []).join(", ")],
            webhookUrl: [config.webhookUrl ?? ""],
            hasWebhookUrl: [config.hasWebhookUrl ?? false],
            chatIdsInput: [(config.chatIds ?? []).join(", ")],
            language: [config.language ?? "en"],
        });
    }

    private buildNotificationChannelsFormArray(channels: NotificationChannelModel[]): UntypedFormArray {
        return this.formBuilder.array((channels ?? []).map((channel) => this.buildNotificationChannelGroup(channel)));
    }

    private buildProjectPayload(): ProjectModel {
        const objectCopy = { ...this.project };
        Object.assign(objectCopy, this.projectForm.value);
        objectCopy.notificationChannels = this.notificationChannelsFormArray.controls.map((control) =>
            this.toNotificationChannel(control.getRawValue()),
        );
        return objectCopy;
    }

    private buildNotificationChannelModel(value: any): NotificationChannelModel {
        return this.toNotificationChannel(value, true);
    }

    private openNotificationChannelPanel(channel?: NotificationChannelModel) {
        return this.matDialog.open(ProjectNotificationChannelAddEditComponent, {
            data: {
                channel,
            },
            panelClass: "right-panel-dialog",
            width: "50vw",
            maxWidth: "100vw",
            height: "100%",
            maxHeight: "100%",
            position: { right: "0", top: "0", bottom: "0" },
            hasBackdrop: true,
            disableClose: false,
        });
    }

    private parseList(value?: string): string[] {
        return (value ?? "")
            .split(/[\n,]/)
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
    }

    private toNotificationChannel(value: any, includeStoredSecretFlags = false): NotificationChannelModel {
        const config: NotificationChannelModel["config"] = {
            emails: this.parseList(value.emailsInput),
            phoneNumbers: this.parseList(value.phoneNumbersInput),
            webhookUrl: value.webhookUrl?.trim() || undefined,
            chatIds: this.parseList(value.chatIdsInput),
            language: value.language?.trim() || undefined,
        };
        if (includeStoredSecretFlags) {
            config.hasWebhookUrl = value.hasWebhookUrl ?? false;
        }

        return {
            id: value.id,
            name: value.name,
            type: value.type,
            enabled: value.enabled !== false,
            config,
        };
    }

    private executeNotificationChannelTest(channel: NotificationChannelModel, retryOnMissingChannel = true) {
        if (!this.project?.id || !channel?.id || this.testingNotificationChannelIds.has(channel.id)) {
            return;
        }

        this.testingNotificationChannelIds.add(channel.id);
        this.projectService.testNotificationChannel(this.project.id, channel.id).subscribe(
            () => {
                this.testingNotificationChannelIds.delete(channel.id);
                if (channel.type !== NotificationChannelModelType.WebPush) {
                    this.toastr.success(
                        this.translation.translateService.instant(
                            "PROJECTS.NotificationChannelTestSent",
                        ),
                    );
                }
            },
            (error) => {
                this.testingNotificationChannelIds.delete(channel.id);
                if (error?.status === 404 && retryOnMissingChannel) {
                    this.retryNotificationChannelTestWithFreshProject(channel);
                    return;
                }

                console.log(error);
                this.toastr.error(
                    error?.error?.message || this.translation.translateService.instant("PROJECTS.NotificationChannelTestFailed"),
                );
            },
        );
    }

    private retryNotificationChannelTestWithFreshProject(channel: NotificationChannelModel) {
        if (!this.project?.id) {
            return;
        }

        this.projectService.get(this.project.id).subscribe(
            (project) => {
                this.updateProjectNotificationChannels(project.notificationChannels ?? []);

                const refreshedChannel = this.findMatchingNotificationChannel(
                    this.project.notificationChannels ?? [],
                    channel,
                );

                if (refreshedChannel?.id && refreshedChannel.id !== channel.id) {
                    this.executeNotificationChannelTest(refreshedChannel, false);
                    return;
                }

                this.toastr.error(
                    this.translation.translateService.instant("PROJECTS.NotificationChannelTestFailed"),
                );
            },
            (error) => {
                console.log(error);
                this.toastr.error(
                    error?.error?.message || this.translation.translateService.instant("PROJECTS.NotificationChannelTestFailed"),
                );
            },
        );
    }

    private findMatchingNotificationChannel(
        channels: NotificationChannelModel[],
        targetChannel: NotificationChannelModel,
    ): NotificationChannelModel | undefined {
        return (
            channels.find((channel) => channel.id === targetChannel.id) ??
            channels.find(
                (channel) =>
                    this.getNotificationChannelFingerprint(channel) ===
                    this.getNotificationChannelFingerprint(targetChannel),
            )
        );
    }

    private getNotificationChannelFingerprint(channel: NotificationChannelModel): string {
        return JSON.stringify({
            name: channel.name?.trim() || "",
            type: channel.type,
            enabled: channel.enabled !== false,
            emails: [...(channel.config?.emails ?? [])].sort(),
            phoneNumbers: [...(channel.config?.phoneNumbers ?? [])].sort(),
            webhookUrl: channel.config?.webhookUrl?.trim() || "",
            chatIds: [...(channel.config?.chatIds ?? [])].sort(),
            language: channel.config?.language?.trim() || "",
        });
    }

    private updateProjectNotificationChannels(notificationChannels: NotificationChannelModel[]) {
        const draftProject = this.buildProjectPayload();
        this.project = this.normalizeProject({
            ...draftProject,
            notificationChannels,
        });
        this.syncNotificationChannelsForm(notificationChannels);
    }

    private syncNotificationChannelsForm(notificationChannels: NotificationChannelModel[]) {
        if (!this.projectForm) {
            return;
        }

        const wasDirty = this.projectForm.dirty;
        const wasTouched = this.projectForm.touched;

        this.syncingProjectForm = true;
        this.projectForm.setControl(
            "notificationChannels",
            this.buildNotificationChannelsFormArray(notificationChannels),
            { emitEvent: false },
        );
        if (wasDirty) {
            this.projectForm.markAsDirty();
        }
        if (wasTouched) {
            this.projectForm.markAllAsTouched();
        }
        this.syncingProjectForm = false;
    }

    private syncProjectForm(project: ProjectModel) {
        if (!this.projectForm) {
            return;
        }

        this.syncingProjectForm = true;
        this.projectForm.patchValue(
            {
                id: project.id,
                name: project.name,
                timezone: project.timezone,
                dateformat: project.dateformat,
            },
            { emitEvent: false },
        );
        this.projectForm.setControl(
            "notificationChannels",
            this.buildNotificationChannelsFormArray(project.notificationChannels ?? []),
            { emitEvent: false },
        );
        this.projectForm.markAsPristine();
        this.syncingProjectForm = false;
    }
}
